import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
// @ts-ignore
import { Banner } from '@els/els-react--banner';
// @ts-ignore
import { Paginator } from '@els/els-react--paginator';
// @ts-ignore
import { Loader } from '@els/els-react--loader';
// @ts-ignore
import { Dropdown } from '@els/els-react--dropdown';
import './ebpc-dashboard.scss';
import { generatePath } from 'react-router';
import NoSkillPage from '../../components/no-skill-page/no-skill-page';
import ServerErrorPage from '../../components/server-error-page/server-error-page';
import EbpcSkillList from './ebpc-dashboard-table/Ebpc-Skill-List';
import type { ISkillDetails } from '../../../libs/utils/interfaces/ebpc-interface';
import {
    selectskillsDetails,
    selectEbpcAsyncStatus,
    selectStatusCode,
} from './ebpc-selector';
import {
    selectReferentialAccessAsyncStatus,
    selectIsIpWhitelistedAsyncStatus,
} from '../referential-access/referential-access-selector';
import {
    isPending,
    hasFailed,
    hasSucceeded,
    isComplete,
} from '../../../libs/utils/constants/async-status';
import { getEbpcSkills } from './ebpc-thunk';
import { ResultsPerPage } from '../../constants/results-per-page';
import { skillsFilterDropdown } from '../../constants/skills-sort-order';
import { sortDataByAlphabet, sortDataByDate } from '../../utils/helper';
import SkillLaunch from '../../components/skill-launch/skill-launch';
import Routes from '../../constants/routes';
import BackButtonNavigationTracking from '../../utils/back-button-helper';
import { IRootState } from '../../app/store';
import BackButton from '../../components/back-button/back-button';
import { cancelledAsyncLogin } from '../login/login-slice';

const EbpcDashboard = () => {
    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedFilter, setSelectedFilter] = useState(
        skillsFilterDropdown[2].label
    );
    const [selectedSortOrder, setselectedSortOrder] = useState('Newest First');
    const [isSorting, setIsSorting] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState<number | null>(
        null
    );
    const skillList: Array<ISkillDetails> = useSelector(selectskillsDetails);
    const ebpcAsyncStatus = useSelector(selectEbpcAsyncStatus);
    const statusCode = useSelector(selectStatusCode);
    const isReferentialAsyncStatus = useSelector(
        selectReferentialAccessAsyncStatus
    );
    const isIpWhitelistedAsyncStatus = useSelector(
        selectIsIpWhitelistedAsyncStatus
    );

    const baseClassName = 'ebpc-dashboard';
    const skillsCount = skillList.length;
    const limit = ResultsPerPage.EBPC_SKILLS_VIEW;
    let offset = (currentPage - 1) * limit;
    let sortedSkills = [];
    const history = useHistory();
    const { ecrId } = useParams<{ ecrId: string }>();

    const handleSkillsSort = (sortOrder: string) => {
        if (sortOrder === 'A to Z' || sortOrder === 'Z to A') {
            return sortDataByAlphabet(skillList, sortOrder);
        }
        return sortDataByDate(skillList, sortOrder);
    };

    const historyStack = useSelector(
        (state: IRootState) => state.navigation.history
    );
    const canGoBack = historyStack.length > 1;

    sortedSkills = handleSkillsSort(selectedSortOrder);
    let paginatedSkillList = sortedSkills.slice(offset, offset + limit);
    let startIndex;

    if (currentPage === 1) {
        if (skillsCount > 0) {
            startIndex = 1;
        } else {
            startIndex = 0;
        }
    } else {
        startIndex = (currentPage - 1) * limit + 1;
    }
    const endIndex = Math.min(currentPage * limit, skillsCount);

    const onPageChangeHandler = (value: number): void => {
        setCurrentPage(value);
        offset = (currentPage - 1) * limit;
        paginatedSkillList = sortedSkills.slice(offset, offset + limit);
        window.scrollTo(0, 0);
    };

    useEffect(() => {
        if (!(localStorage.getItem('IS_USER_AUTHENTICATED') === 'true')) {
            dispatch(cancelledAsyncLogin());
        }
        dispatch(getEbpcSkills());
    }, []);

    const getSelectedOption = (selectedSortBy: any) => {
        const selectedLabel = selectedSortBy.label;
        const selectedOption = skillsFilterDropdown.find(
            (option: any) => option.label === selectedLabel
        );
        if (selectedOption) {
            const { label, value } = selectedOption;
            setSelectedFilter(label);
            setselectedSortOrder(value);
            setIsSorting(true);
            handleSkillsSort(value);
            setTimeout(() => {
                setIsSorting(false);
            }, 1000);
        }
    };

    // need to update this condition to check for the correct status code 500
    const errorType = hasFailed(ebpcAsyncStatus) && statusCode !== 200;

    const handleRedirectToSkillDetail = (courseId: any) => {
        setSelectedCourseId(courseId);
        const redirectLink = generatePath(Routes.SKILL_LAUNCH, {
            id: courseId,
            ecrId,
        });
        history.push(`${redirectLink}/`);
        window.scrollTo(0, 0);
    };

    return (
        <div>
            {canGoBack && window.history.length > 0 && <BackButton />}
            {selectedCourseId ? (
                <SkillLaunch id={selectedCourseId} />
            ) : (
                <div className={`${baseClassName}`}>
                    {errorType ? (
                        <ServerErrorPage />
                    ) : (
                        <div>
                            <h2
                                id="content"
                                tabIndex={-1}
                                className={`${baseClassName}__sub-header`}
                            >
                                Evidence Based Practice Changes
                            </h2>
                        </div>
                    )}
                    {isComplete(isReferentialAsyncStatus) &&
                        isComplete(isIpWhitelistedAsyncStatus) &&
                        isPending(ebpcAsyncStatus) && (
                            <Loader
                                isVisible
                                isBlocking={false}
                                className={`${baseClassName}-loader`}
                            />
                        )}
                    {hasSucceeded(ebpcAsyncStatus) && (
                        <>
                            <Banner
                                className={`${baseClassName}__ebpc-banner`}
                                isVisible
                                isFilled
                            >
                                <b>Evidence Based Practice Changes</b>
                                <span>
                                    &nbsp;are any Skills you have licensed that
                                    have had clinical practice changes made in
                                    the past 3 months.
                                </span>
                            </Banner>

                            {skillList?.length ? (
                                <>
                                    <div className={`${baseClassName}__sortBy`}>
                                        <span
                                            className={`${baseClassName}__sortBy__span-label`}
                                        >
                                            Sort by:
                                        </span>
                                        <Dropdown
                                            id="dropdown"
                                            options={skillsFilterDropdown}
                                            label={selectedFilter}
                                            className={`${baseClassName}__sortBy-dropdown`}
                                            onChange={getSelectedOption}
                                            placement="bottom-end"
                                            shouldCloseOnESC
                                        />
                                    </div>
                                    <div
                                        className={`${baseClassName}__details`}
                                    >
                                        {`Showing ${startIndex} - ${endIndex} of ${skillsCount} ${
                                            skillsCount !== 1
                                                ? 'Evidence Based Practice Changes'
                                                : 'Evidence Based Practice Change'
                                        }`}
                                    </div>
                                    <div
                                        className={`${baseClassName}__skill-warpper`}
                                    >
                                        <EbpcSkillList
                                            id="ebpc-skill-list"
                                            skillList={paginatedSkillList}
                                            isSorting={isSorting}
                                            handleRedirectToSkillDetail={
                                                handleRedirectToSkillDetail
                                            }
                                        />
                                        <div className="pagination-container">
                                            <Paginator
                                                a11y={{
                                                    name: 'EBPC-Skill-Page',
                                                    pagingUnitName: 'Page',
                                                    morePagingUnit:
                                                        'More pages',
                                                }}
                                                shouldShowPrevNextLabel
                                                className="els-pagination"
                                                currentPage={currentPage}
                                                onPageChange={
                                                    onPageChangeHandler
                                                }
                                                resultsPerPage={limit}
                                                maxPagesToShow={10}
                                                totalResults={skillsCount}
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className={`${baseClassName}__details`}>
                                    <NoSkillPage />
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
export default BackButtonNavigationTracking(
    'Evidence Based Practice',
    EbpcDashboard
);
